import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SafeUrl } from '@angular/platform-browser';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CardComponent } from '../card/card.component';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import { FullUserProfile } from '../../models/user.model';
import { formatDateLocalized } from '../../utils/date-utils';
import { getJobPositions } from '../../../../../../libs/shared/src/lib/i18n/job-positions/index';
import { getJobTypes } from '../../../../../../libs/shared/src/lib/i18n/job-types/index';
import { getSelfDescriptions } from '../../../../../../libs/shared/src/lib/i18n/self-descriptions/index';
import { PhoneFormatPipe } from '../../utils/phone-format.pipe';
import { ConsentType } from '../../../../../../libs/shared/src/lib/models/consent.model';

@Component({
  selector: 'app-profile-personal-info-preview',
  standalone: true,
  imports: [CommonModule, TranslocoModule, CardComponent, PhoneFormatPipe, UserAvatarComponent],
  templateUrl: './profile-personal-info-preview.component.html',
  styleUrls: ['./profile-personal-info-preview.component.scss'],
})
export class ProfilePersonalInfoPreviewComponent {
  @Input() userProfile: FullUserProfile | null = null;
  @Input() isLoading = false;
  @Input() isAuthenticated: boolean = false; // Track if user is logged in
  @Input() userEmail: string = '';
  @Input() educationLevelOptions: { value: number; label: string }[] = [];
  // Avatar (rendered at the top of the Essentials section; editable in all modes)
  @Input() avatarPhoto: SafeUrl | string | null = null;
  @Input() avatarFirstName = '';
  @Input() avatarLastName = '';
  @Input() avatarIdentifier = '';
  @Input() avatarLoading = false;
  @Output() avatarEdit = new EventEmitter<void>();
  @Output() avatarRemove = new EventEmitter<void>();
  readonly ConsentType = ConsentType;

  constructor(private translocoService: TranslocoService) {}

  /**
   * Get display text for job types (employment types)
   */
  getJobTypeDisplay(): string {
    // Use new employmentTypes field from backend
    const employmentTypes = this.userProfile?.employmentTypes;
    if (!employmentTypes || !Array.isArray(employmentTypes) || employmentTypes.length === 0) {
      return '';
    }

    try {
      // Use shared job types translations
      const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
      const jobTypesData = getJobTypes(currentLang);

      const translatedTypes = employmentTypes.map((et: any) => {
        const key = et.employmentTypeKey;
        return (jobTypesData.jobTypes as any)[key] || key;
      });
      return translatedTypes.join(', ');
    } catch {
      return '';
    }
  }

  /**
   * Get display text for job categories
   */
  getJobCategoryDisplay(): string {
    // Use new jobCategories field from backend
    const jobCategories = this.userProfile?.jobCategories;
    if (!jobCategories || !Array.isArray(jobCategories) || jobCategories.length === 0) {
      return '';
    }

    try {
      // Use shared job categories translations
      const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
      const jobPositions = getJobPositions(currentLang);

      const translatedCategories = jobCategories.map((jc: any) => {
        const key = jc.categoryKey;
        return (jobPositions.jobCategories as any)[key] || key;
      });
      return translatedCategories.join(', ');
    } catch {
      return '';
    }
  }

  /**
   * Get display text for job positions
   */
  getJobPositionDisplay(): string {
    // Use new jobPositions field from backend
    const jobPositionsData = this.userProfile?.jobPositions;
    if (!jobPositionsData || !Array.isArray(jobPositionsData) || jobPositionsData.length === 0) {
      return '';
    }

    try {
      // Use shared job positions translations
      const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
      const jobPositions = getJobPositions(currentLang);

      const translatedPositions = jobPositionsData.map((jp: any) => {
        const key = jp.positionKey;
        return (jobPositions.jobPositions as any)[key] || key;
      });
      return translatedPositions.join(', ');
    } catch {
      return '';
    }
  }

  /**
   * Get display text for self descriptions
   */
  getSelfDescriptionDisplay(): string {
    // Handle both lowercase and uppercase variants (backend inconsistency)
    const selfDescriptionField = (this.userProfile as any)?.SelfDescription || this.userProfile?.selfDescription;
    if (!selfDescriptionField) return '';

    try {
      const descriptions = typeof selfDescriptionField === 'string' ? JSON.parse(selfDescriptionField) : selfDescriptionField;

      if (!Array.isArray(descriptions) || descriptions.length === 0) return '';

      // Use shared self descriptions translations
      const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
      const selfDescriptionsData = getSelfDescriptions(currentLang);

      const translatedDescriptions = descriptions.map((desc) => {
        return (selfDescriptionsData.selfDescriptions as any)[desc] || desc;
      });
      return translatedDescriptions.join(', ');
    } catch {
      return '';
    }
  }

  /**
   * Get education level display text
   */
  getEducationLevelDisplay(): string {
    const levelId = this.userProfile?.educationLevelID;
    if (!levelId) return '';
    const option = this.educationLevelOptions.find(o => o.value === levelId);
    return option?.label || '';
  }

  /**
   * Get gender label for display
   */
  getGenderLabel(gender: string | undefined): string {
    if (!gender) return '';

    const genderMap: { [key: string]: string } = {
      'db.male': 'jobApply.personal.male',
      'db.female': 'jobApply.personal.female',
      'db.other': 'jobApply.personal.other',
      // For backward compatibility
      male: 'jobApply.personal.male',
      female: 'jobApply.personal.female',
      other: 'jobApply.personal.other',
    };

    return genderMap[gender] || '';
  }

  /**
   * Format date for localized display
   * Using centralized date utility function
   */
  formatDate(dateString: string | undefined): string {
    return formatDateLocalized(dateString, this.translocoService.getActiveLang());
  }
}
